import Typography from "antd/lib/typography";
import Link from "next/link";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { HtmlRichTextRenderer } from "src/components/inputs/RichText";

import { useAuthenticatedUser } from "@sol/authentication/AuthenticatedUserContext";
import { Route } from "@sol/routing";
import { IAnnouncementResource, useAnnouncementListQuery, useUserSettingsUpdateMutation } from "@sol/sdk";
import { ChevronDown } from "@sol/uikit/core/icons/Arrows/ChevronDown";
import { ChevronLeft } from "@sol/uikit/core/icons/Arrows/ChevronLeft";
import { ChevronRight } from "@sol/uikit/core/icons/Arrows/ChevronRight";
import { ChevronUp } from "@sol/uikit/core/icons/Arrows/ChevronUp";
import { Cross } from "@sol/uikit/core/icons/Cross";
import { Card } from "@sol/uikit/data-display/Card/Card";
import { Tag } from "@sol/uikit/data-display/Tag/Tag";
import { Button } from "@sol/uikit/general/Button/Button";

import { formatAnnouncementDate } from "./AnnouncementCard";
import styles from "./AnnouncementCard.module.css";
import { getAnnouncementVideoEmbedUrl } from "../lib/getAnnouncementVideoEmbedUrl";

type Props = {
    className?: string;
};

const getUnreadAnnouncements = (
    announcementItems: IAnnouncementResource[],
    dismissedAnnouncementUuid: string | null,
) => {
    if (!dismissedAnnouncementUuid) {
        return announcementItems;
    }

    const dismissedAnnouncementIndex = announcementItems.findIndex(
        announcement => announcement.uuid === dismissedAnnouncementUuid,
    );

    return dismissedAnnouncementIndex === -1
        ? announcementItems
        : announcementItems.slice(0, dismissedAnnouncementIndex);
};

const WIDGET_ANNOUNCEMENTS_PER_PAGE = 5;
const WIDGET_ANNOUNCEMENTS_REFETCH_INTERVAL = 60_000;

export const Announcements = ({ className }: Props) => {
    const [t] = useTranslation();
    const { user, settings, refetchSettings } = useAuthenticatedUser();
    const { data: announcements, isLoading } = useAnnouncementListQuery(
        { pagination: { page: 1, perPage: WIDGET_ANNOUNCEMENTS_PER_PAGE } },
        {
            refetchInterval: WIDGET_ANNOUNCEMENTS_REFETCH_INTERVAL,
            refetchOnWindowFocus: true,
        },
    );
    const { mutateAsync: updateUserSettings, isPending } = useUserSettingsUpdateMutation();
    const [dismissedAnnouncementUuid, setDismissedAnnouncementUuid] = useState(settings.dismissedAnnouncementUuid);
    const [activeSlide, setActiveSlide] = useState(0);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isVideoLoaded, setIsVideoLoaded] = useState(false);

    const announcementItems = announcements?.["hydra:member"] ?? [];
    const unreadAnnouncements = getUnreadAnnouncements(announcementItems, dismissedAnnouncementUuid);
    const latestUnreadAnnouncement = unreadAnnouncements[0];
    const activeAnnouncement = unreadAnnouncements[activeSlide] ?? unreadAnnouncements[0];
    const activeAnnouncementEmbedUrl = activeAnnouncement
        ? getAnnouncementVideoEmbedUrl(activeAnnouncement.videoUrl)
        : null;
    const detailsId = activeAnnouncement ? `announcement-details-${activeAnnouncement.uuid}` : undefined;
    const hasMultipleAnnouncements = unreadAnnouncements.length > 1;

    useEffect(() => {
        setDismissedAnnouncementUuid(settings.dismissedAnnouncementUuid);
    }, [settings.dismissedAnnouncementUuid]);

    useEffect(() => {
        if (activeSlide >= unreadAnnouncements.length) {
            setActiveSlide(0);
        }
    }, [activeSlide, unreadAnnouncements.length]);

    useEffect(() => {
        setIsVideoLoaded(false);
    }, [activeAnnouncement?.uuid]);

    const handleDismissAnnouncement = async () => {
        if (!latestUnreadAnnouncement) {
            return;
        }

        const previousDismissedAnnouncementUuid = dismissedAnnouncementUuid;
        setDismissedAnnouncementUuid(latestUnreadAnnouncement.uuid);
        setActiveSlide(0);

        try {
            await updateUserSettings({
                user,
                homecards: settings.homecards,
                dismissedAnnouncementUuid: latestUnreadAnnouncement.uuid,
            });
            await refetchSettings();
        } catch {
            setDismissedAnnouncementUuid(previousDismissedAnnouncementUuid);
        }
    };

    const handlePreviousAnnouncement = () => {
        setActiveSlide(current => (current === 0 ? unreadAnnouncements.length - 1 : current - 1));
    };

    const handleNextAnnouncement = () => {
        setActiveSlide(current => (current + 1) % unreadAnnouncements.length);
    };

    if (isLoading || !latestUnreadAnnouncement || unreadAnnouncements.length === 0 || !activeAnnouncement) {
        return null;
    }

    return (
        <section className={className} aria-labelledby="latest-announcement-title">
            <div className="px-ant pb-ant">
                <div className="relative">
                    <Card
                        classNames={{ body: "!p-0" }}
                        className="overflow-visible shadow-none"
                        data-cy="latest-announcement"
                    >
                        <div className="flex min-w-0 flex-col">
                            <span id="latest-announcement-title" className="sr-only">
                                {t("page.announcements.latestTitle")}
                            </span>

                            <div className="flex min-w-0 flex-wrap items-center gap-x-ant-sm gap-y-ant-xs p-ant-sm md:flex-nowrap">
                                {hasMultipleAnnouncements && (
                                    <Button
                                        type="text"
                                        size="small"
                                        onClick={handlePreviousAnnouncement}
                                        aria-label={t("page.announcements.actions.previous")}
                                        icon={<ChevronLeft />}
                                        className="md:order-1"
                                    />
                                )}
                                <Tag
                                    bordered={false}
                                    className="hidden font-ant-strong uppercase md:order-2 md:inline-block"
                                    color="purple"
                                >
                                    {t("tag.new")}
                                </Tag>
                                <Typography.Text strong className="min-w-0 flex-1 truncate md:order-3">
                                    {activeAnnouncement.title}
                                </Typography.Text>
                                <Typography.Text type="secondary" className="whitespace-nowrap md:order-4">
                                    {formatAnnouncementDate(activeAnnouncement.appearanceDate)}
                                </Typography.Text>

                                {hasMultipleAnnouncements && (
                                    <>
                                        <Typography.Text type="secondary" className="whitespace-nowrap md:order-6">
                                            ({activeSlide + 1}/{unreadAnnouncements.length})
                                        </Typography.Text>
                                        <Button
                                            type="text"
                                            size="small"
                                            onClick={handleNextAnnouncement}
                                            aria-label={t("page.announcements.actions.next")}
                                            icon={<ChevronRight />}
                                            className="md:order-7"
                                        />
                                    </>
                                )}

                                <Button
                                    type="primary"
                                    onClick={() => setIsExpanded(current => !current)}
                                    aria-controls={detailsId}
                                    aria-expanded={isExpanded}
                                    className="order-last w-full md:order-5 md:w-auto"
                                >
                                    {t("page.announcements.actions.read")}
                                    {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                </Button>
                            </div>

                            {isExpanded && (
                                <div id={detailsId} className="border-t border-ant-color p-ant-sm md:p-ant">
                                    <div className="flex flex-col gap-ant">
                                        <div
                                            className={`grid grid-cols-1 gap-ant ${activeAnnouncementEmbedUrl ? "md:grid-cols-2" : ""}`}
                                        >
                                            <article>
                                                <HtmlRichTextRenderer
                                                    className={styles.content}
                                                    html={activeAnnouncement.body}
                                                />
                                            </article>
                                            {activeAnnouncementEmbedUrl && (
                                                <div className="relative aspect-video w-full overflow-hidden rounded-ant-lg">
                                                    {!isVideoLoaded && (
                                                        <div
                                                            aria-hidden="true"
                                                            className="absolute inset-0 animate-pulse bg-ant-fill-secondary"
                                                        />
                                                    )}
                                                    <iframe
                                                        src={activeAnnouncementEmbedUrl}
                                                        onLoad={() => setIsVideoLoaded(true)}
                                                        className="size-full border-0"
                                                        allowFullScreen
                                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                        title={activeAnnouncement.title}
                                                    />
                                                </div>
                                            )}
                                        </div>

                                        <div className="flex justify-end">
                                            <Link href={Route.ANNOUNCEMENTS} passHref>
                                                <Button type="primary">
                                                    {t("page.announcements.actions.viewAll")}
                                                </Button>
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </Card>

                    <Button
                        shape="circle"
                        size="small"
                        onClick={handleDismissAnnouncement}
                        loading={isPending}
                        aria-label={t("page.announcements.actions.close")}
                        icon={<Cross />}
                        className="absolute! top-0 right-0 z-10 translate-x-1/2 -translate-y-1/2 bg-ant-layout"
                    />
                </div>
            </div>
        </section>
    );
};

export default Announcements;
